import { IconFont, singleTextEllipsis } from '@byecode/ui'
import { Checkbox } from '@mantine/core'
import React, { useMemo } from 'react'
import styled, { css } from 'styled-components'

import { useApplicationContext } from '../../contexts'
import type { UserOption } from '../../types'
import { getColorById, getImageFullUrlInApplication } from '../../utils'

interface SelectItemPreviewerProps {
    enableCheckBox?: boolean
    data: UserOption
    value?: string[]
    textStyles?: React.CSSProperties
    extraText?: string
    department?: string
    color?: string
}

const SCxCellValue = styled.div`
    padding: 0 12px;
    display: flex;
    width: 100%;
    gap: 16px;
    align-items: center;
`

const SCxLeft = styled.div`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-flow: row nowrap;
`

const SCxMeta = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
`

const SCxRight = styled.div`
    flex-shrink: 0;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-flow: row nowrap;
    max-width: 50%;
`

const SCxAvatar = styled.img`
    width: 24px;
    height: 24px;
    min-width: 24px;
    margin-right: 8px;
    border-radius: 50%;
`

const SCxIcon = styled(IconFont)`
    margin-right: 4px;
`

const SCxAvatarText = styled.div<{ background?: string }>`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    min-width: 24px;
    height: 24px;
    font-size: var(--font-size-sm);
    margin-right: 8px;
    border-radius: 50%;
    overflow: hidden;
    color: var(--color-white);
    background-color: ${({ background }) => background || 'var(--color-gray-2)'};
`

const SCxTitle = styled.div<{ $stack?: boolean }>`
    min-width: 0;
    width: 100%;
    height: ${({ $stack }) => ($stack ? 'auto' : '100%')};
    ${({ $stack }) => ($stack ? 'flex-shrink: 0; line-height: 22px;' : '')}
    ${singleTextEllipsis}
`

const SCxDept = styled.div`
    flex-shrink: 0;
    min-width: 0;
    margin-top: 2px;
    font-size: var(--font-size-sm);
    line-height: 16px;
    color: var(--color-gray-400);
    ${singleTextEllipsis}
`

const SCxText = styled.div`
    width: 100%;
    height: 100%;
    color: var(--color-gray-500);
    text-align: right;
    ${singleTextEllipsis}
`

const SCxCheckbox = styled(Checkbox)<{ color?: string }>`
    margin-top: 2px;
    margin-right: 10px;

    .mantine-Checkbox-input:checked {
        background-color: ${({ color }) => color || 'var(--color-main)'};
        border-color: ${({ color }) => color || 'var(--color-main)'};
    }
`

export const SelectItemPreviewer: React.FC<SelectItemPreviewerProps> = ({
    data,
    value,
    enableCheckBox = false,
    color,
    extraText,
    department
}) => {
    const { id, name, avatar } = data
    const background = getColorById(id)
    const { appId } = useApplicationContext()
    const avatarContent = useMemo(() => {
        if (id === '{currentUserId}') {
            return <SCxIcon size={24} type="UserCircle" />
        }
        if (avatar) {
            return <SCxAvatar src={getImageFullUrlInApplication(appId, avatar)} />
        }
        return <SCxAvatarText background={background}>{name.charAt(0)}</SCxAvatarText>
    }, [appId, avatar, background, id, name])

    return (
        <SCxCellValue key={id}>
            <SCxLeft>
                {enableCheckBox && <SCxCheckbox color={color} size="xs" checked={value?.includes(id)} readOnly />}
                {avatarContent}
                {department ? (
                    <SCxMeta>
                        <SCxTitle $stack>{name}</SCxTitle>
                        <SCxDept>{department}</SCxDept>
                    </SCxMeta>
                ) : (
                    <SCxTitle>{name}</SCxTitle>
                )}
            </SCxLeft>
            {extraText && (
                <SCxRight>
                    <SCxText>{extraText}</SCxText>
                </SCxRight>
            )}
        </SCxCellValue>
    )
}
